import React, { useState, useEffect } from 'react';
import { StudentHeader } from '../st-components/HeaderStudent';
import { AttendanceSection } from '../st-components/AttendanceSection';
import { PerformanceSection } from '../st-components/PerformanceSection';
import { PersonalCabinet } from '../st-components/PersonalCabinet';
import { DocumentsSection } from '../st-components/DocumentSection';
import { useUser, Student } from '../context/UserContext';
import './StudentStyle.css';
import { ScheduleSection } from '../st-components/ScheduleSectionST';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService, GroupData, TeacherData, StudentMark, SubjectAttendance, calculateOverallAttendancePercentage } from '../services/studentApiService';

export const StudentPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('attendance');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [groupData, setGroupData] = useState<GroupData | null>(null);
  const [curatorData, setCuratorData] = useState<TeacherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isStudent } = useUser();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [attendancePercentage, setAttendancePercentage] = useState<number>(0);
  const [averageGrade, setAverageGrade] = useState<number>(0);
  const [studentMarks, setStudentMarks] = useState<StudentMark[]>([]);
  const [attendanceData, setAttendanceData] = useState<SubjectAttendance[]>([]);
  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(false);

  // Определение темы при загрузке
  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem('st-theme');
      const isDark = savedTheme === 'dark' || (!savedTheme && document.body.classList.contains('st-theme-dark'));
      setIsDarkTheme(isDark);
    };

    checkTheme();
    
    // Слушатель изменений темы
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkTheme();
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  // Синхронизация активной вкладки с URL параметрами
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['personal',  'schedule', 'attendance', 'performance','documents'].includes(tab)) {
      setActiveTab(tab);
    } else {
      setActiveTab('personal');
      searchParams.set('tab', 'personal');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    searchParams.set('tab', tab);
    setSearchParams(searchParams);
  };

  const formatCuratorName = (curator: TeacherData | null) => {
    if (!curator) return '';
    
    const lastName = curator.lastName || '';
    const firstName = curator.name ? `${curator.name.charAt(0)}.` : '';
    const middleName = curator.patronymic ? `${curator.patronymic.charAt(0)}.` : '';
    
    return `${lastName} ${firstName}${middleName}`.trim();
  };

  // ФУНКЦИЯ РАСЧЕТА СРЕДНЕГО БАЛЛА 
  const calculateAverageGrade = (marks: StudentMark[]): number => {
    if (!marks || marks.length === 0) {
      return 0;
    }

    let totalGrade = 0;
    let gradeCount = 0;

    marks.forEach((subject, subjectIndex) => {
      if (!subject) {
        return;
      }

      if (subject.marksBySt && Array.isArray(subject.marksBySt)) {
        subject.marksBySt.forEach((mark, markIndex) => {
          if (mark && mark.value !== null && mark.value !== undefined) {
            const gradeValue = mark.value;
            
            if (gradeValue >= 2 && gradeValue <= 5) {
              totalGrade += gradeValue;
              gradeCount++;
            }
          }
        });
      }
    });

    const result = gradeCount > 0 ? parseFloat((totalGrade / gradeCount).toFixed(1)) : 0;
    return result;
  };

  // ФУНКЦИЯ РАСЧЕТА ПРОЦЕНТА ПОСЕЩАЕМОСТИ
  const calculateAttendancePercentage = (attendanceData: SubjectAttendance[]): number => {
    return calculateOverallAttendancePercentage(attendanceData);
  };

  // Загрузка данных студента
  useEffect(() => {
    const loadStudentData = async () => {
      if (!user || !isStudent) {
        navigate('/login');
        return;
      }

      const student = user as Student;
      console.log('Данные студента:', student);
      
      setLoading(true);
      setError(null);

      try {
        const groupId = student.idGroup;

        if (!groupId) {
          throw new Error('ID группы не найден в данных студента');
        }

        // Загружаем данные группы
        const groupDataResponse = await apiService.getGroupData(groupId);
        setGroupData(groupDataResponse);

        // Загружаем данные куратора
        if (groupDataResponse.idCurator) {
          try {
            const curatorDataResponse = await apiService.getTeacherData(groupDataResponse.idCurator);
            setCuratorData(curatorDataResponse);
          } catch (curatorError) {
            setCuratorData(null);
          }
        } else {
          setCuratorData(null);
        }

        // Загружаем данные успеваемости
        try {
          const marksData = await apiService.getStudentMarks(student.id);
          setStudentMarks(marksData || []);

          const avgGrade = calculateAverageGrade(marksData || []);
          setAverageGrade(avgGrade);
        } catch (marksError) {
          console.error('Ошибка загрузки оценок:', marksError);
          setAverageGrade(0.0);
        }

        // Загружаем данные посещаемости для расчета процента
        try {
          const attendanceDataResponse = await apiService.getStudentAttendance(student.id);
          setAttendanceData(attendanceDataResponse || []);

          const attendancePercent = calculateAttendancePercentage(attendanceDataResponse || []);
          setAttendancePercentage(attendancePercent);
        } catch (attendanceError) {
          console.error('Ошибка загрузки посещаемости:', attendanceError);
          setAttendancePercentage(0);
        }

      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
        setError(err instanceof Error ? err.message : 'Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };

    loadStudentData();
  }, [user, isStudent, navigate]);

  // Получение иконок в зависимости от темы и активной вкладки
  const getTabIcon = (tabName: string) => {
    const icons = {
      attendance: '/st-icons/attendance_icon.svg',
      performance: '/st-icons/grade_icon.svg',
      personal: '/st-icons/cabinet_icon.svg',
      schedule: '/st-icons/schedule_icon.svg',
      documents: '/st-icons/documents_icon.svg'
    };
    return <img src={icons[tabName as keyof typeof icons]} alt="" className="st-nav-svg-icon" />;
  };

  // Получение иконки для заголовка контента (меняется в зависимости от темы)
  const getTitleIcon = (tabName: string) => {
    // Для светлой темы используем синие иконки из боковой панели
    if (!isDarkTheme) {
      const icons = {
        attendance: '/st-icons/attendance_icon.svg',
        performance: '/st-icons/grade_icon.svg',
        personal: '/st-icons/cabinet_icon.svg',
        schedule: '/st-icons/schedule_icon.svg',
        documents: '/st-icons/documents_icon.svg'
      };
      return <img src={icons[tabName as keyof typeof icons]} alt="" className="st-title-icon" />;
    }
    
    // Для темной темы используем белые иконки
    const whiteIcons = {
      attendance: '/st-icons/white_attendance_icon.svg',
      performance: '/st-icons/white_grade_icon.svg',
      personal: '/st-icons/white_cabinet_icon.svg',
      schedule: '/st-icons/white_schedule_icon.svg',
      documents: '/st-icons/white_documents_icon.svg'
    };
    return <img src={whiteIcons[tabName as keyof typeof whiteIcons]} alt="" className="st-title-icon" />;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div className="loading-spinner"></div>
          <p style={{ marginTop: '20px', color: '#64748b' }}>Загрузка данных...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
          <h3>Ошибка загрузки данных</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Обновить страницу
          </button>
        </div>
      );
    }

    const student = user as Student;

    switch (activeTab) {
      case 'attendance':
        return (
          <AttendanceSection 
            studentId={student.id}
            groupNumber={student.numberGroup?.toString()}
          />
        );
      case 'performance':
        return <PerformanceSection studentId={student.id} />;
      case 'personal':
        return <PersonalCabinet />;
      case 'schedule':
        return <ScheduleSection />;
      case 'documents':
        return <DocumentsSection />;
      default:
        return <AttendanceSection studentId={student.id}/>;
    }
  };

  const getTabTitle = (tabName: string) => {
    const titles = {
      attendance: 'Посещаемость',
      performance: 'Успеваемость',
      personal: 'Личный кабинет',
      schedule: 'Расписание',
      documents: 'Документы'
    };
    return titles[tabName as keyof typeof titles] || 'Посещаемость';
  };

  const getTabSubTitle = (tabName: string) => {
    const subtitles = {
      attendance: 'Мониторинг вашего присутствия на занятиях',
      performance: 'Ваши академические достижения и оценки',
      personal: 'Управление вашими персональными данными',
      schedule: 'Просмотр расписания ваших занятий',
      documents: 'Управление вашими документами и заявлениями'
    };
    return subtitles[tabName as keyof typeof subtitles] || 'Мониторинг вашего присутствия на занятиях';
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(prev => !prev);
  };

  if (!user || !isStudent) {
    return (
      <div className="st-container">
        <div className="st-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Загрузка данных пользователя...</p>
          </div>
        </div>
      </div>
    );
  }

  const student = user as Student;

  return (
    <div className="st-container">
      <div className="st-background-animation">
        <div className="st-shape st-shape-1"></div>
        <div className="st-shape st-shape-2"></div>
        <div className="st-shape st-shape-3"></div>
      </div>

      <div className="st-content">
        <StudentHeader />

        <div className={`st-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <aside className={`st-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <button
              className="st-sidebar-toggle"
              onClick={handleSidebarToggle}
              aria-label={sidebarCollapsed ? 'Развернуть панель' : 'Свернуть панель'}
            >
              <img 
                src="/toggle_back.svg" 
                alt="" 
                className={`st-nav-toggle-icon ${sidebarCollapsed ? 'rotated' : ''}`}
              />
            </button>

            <div className="st-sidebar-header">
              <div className="st-user-info">
                <p className="st-user-name">{student.lastName} {student.name}</p>
                <p className="st-user-patronymic">{student.patronymic}</p>
                <p className="st-user-role">Студент</p>
              </div>
            </div>

            <nav className="st-sidebar-nav">
              {['personal', 'schedule', 'attendance', 'performance', 'documents'].map((tab) => (
                <button
                  key={tab}
                  className={`st-nav-item ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab)}
                  data-tooltip={sidebarCollapsed ? getTabTitle(tab) : ''}
                  disabled={loading}
                >
                  <span className="st-nav-icon">{getTabIcon(tab)}</span>
                  <span className="st-nav-text">{getTabTitle(tab)}</span>
                  {activeTab === tab && !sidebarCollapsed && <div className="st-nav-indicator"></div>}
                </button>
              ))}
            </nav>

            <div className="st-sidebar-footer">
              <div className="st-quick-stats">
                <div className="st-stat-item">
                  <p className="st-stat-value">
                    {loading ? '...' : `${attendancePercentage}%`}
                  </p>
                  <p className="st-stat-label">Посещаемость</p>
                </div>
                <div className="st-stat-item">
                  <p className="st-stat-value">
                    {loading ? '...' : (averageGrade > 0 ? averageGrade.toFixed(1) : '0.0')}
                  </p>
                  <p className="st-stat-label">Средний балл</p>
                </div>
              </div>
            </div>
          </aside>

          <main className="st-content-area">
            <div className="st-content-header">
              <div className="st-content-title-wrapper">
                <span className="st-title-icon">{getTitleIcon(activeTab)}</span>
                <div>
                  <h1 className="st-content-title">{getTabTitle(activeTab)}</h1>
                  <p className="st-content-subtitle">{getTabSubTitle(activeTab)}</p>
                </div>
              </div>
            </div>

            <div className="st-content-card">
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};